import styled from '@emotion/styled/macro';
import { BaseButton, Variables, forPhoneOnly } from '@enter/core';

export const OverlayBody = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    min-height: 100%;
    background: ${Variables.Colors.n15};
`;

export const Toolbar = styled.div`
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    background: ${Variables.Colors.n100};
    flex-wrap: wrap;

    ${forPhoneOnly(`
        padding: 12px 16px;
    `)};

    @media print {
        display: none !important;
    }
`;

export const ToolbarTitle = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${Variables.Swatches.White};
`;

export const ToolbarActions = styled.div`
    margin-left: auto;
    display: flex;
    gap: 10px;
    align-items: center;
`;

export const ToolbarGhostButton = styled(BaseButton)`
    height: 36px;
    padding: 0 14px;
    background: transparent !important;
    border: 1px solid ${Variables.Colors.n30} !important;
    color: ${Variables.Swatches.White} !important;
    border-radius: 8px;
`;

export const ToolbarOutlinedButton = styled(BaseButton)`
    height: 36px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent !important;
    border: 1px solid ${Variables.Colors.n30} !important;
    color: ${Variables.Swatches.White} !important;
    border-radius: 8px;
`;

export const ToolbarPrimaryButton = styled(BaseButton)`
    height: 36px;
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${Variables.Colors.b300} !important;
    border: none !important;
    color: ${Variables.Swatches.White} !important;
    border-radius: 8px;
`;

export const PaperWrap = styled.div`
    padding: 28px 24px 56px;
    display: flex;
    justify-content: center;

    @media print {
        padding: 0 !important;
    }
`;

export const StatusChip = styled.span<{ $status?: string }>`
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border-radius: 5px;
    padding: 4px 9px;
    border: 1px solid
        ${p =>
            p.$status === 'paid'
                ? Variables.ThemeLight.SuccessMild1
                : p.$status === 'ready'
                  ? Variables.ThemeLight.PrimaryMild1
                  : Variables.ThemeLight.SuccessMild1};
    background: ${p =>
        p.$status === 'paid'
            ? Variables.ThemeLight.SuccessMild3
            : p.$status === 'ready'
              ? Variables.ThemeLight.PrimaryMild2
              : Variables.ThemeLight.SuccessMild3};
    color: ${p =>
        p.$status === 'paid'
            ? Variables.ThemeLight.SuccessInverted3
            : p.$status === 'ready'
              ? Variables.ThemeLight.PrimaryInverted3
              : Variables.ThemeLight.SuccessInverted3};
`;

/** Statement card — same styling as the Chris invoice statement sheet. */
export const Sheet = styled.div`
    display: flex;
    flex-direction: column;
    width: 760px;
    max-width: 100%;
    min-height: 11in;
    background: ${Variables.ThemeLight.Background3};
    border: 1px solid ${Variables.ThemeLight.Border1};
    border-radius: 14px;
    overflow: hidden;

    @media print {
        border: none !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        width: 100% !important;
        max-width: none !important;
        min-height: 10in !important;
        overflow: visible !important;
    }
`;

export const SheetHeader = styled.div`
    padding: 34px 40px 26px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    flex-wrap: wrap;
`;

export const Brand = styled.div`
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: ${Variables.ThemeLight.PrimaryConstant6};
    margin-bottom: 12px;
`;

export const ProviderName = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const Muted = styled.div`
    font-size: 13px;
    line-height: 1.6;
    color: ${Variables.ThemeLight.TextMild};
`;

export const SheetPad = styled(Muted)`
    padding: 40px;
`;

export const SheetHeaderRight = styled.div`
    text-align: right;
    flex: none;
`;

export const SheetTitle = styled.div`
    font-size: 20px;
    font-weight: 600;
    line-height: 1.1;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const MetaGrid = styled.div`
    margin-top: 16px;
    display: grid;
    grid-template-columns: auto auto;
    gap: 4px 16px;
    font-size: 13px;
    color: ${Variables.ThemeLight.TextMild};
    text-align: right;

    strong {
        color: ${Variables.ThemeLight.TextStrong};
        font-weight: 600;
        font-variant-numeric: tabular-nums;
    }
`;

/** Visit summary (and similar): label + value, left-aligned like Chris mockup. */
export const VisitMetaGrid = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 5px 14px;
    font-size: 13px;
    line-height: 1.4;
    color: ${Variables.ThemeLight.TextMild};
    text-align: left;

    strong {
        color: ${Variables.ThemeLight.TextStrong};
        font-weight: 500;
        font-variant-numeric: tabular-nums;
    }
`;

export const TwoCol = styled.div`
    padding: 24px 40px;
    display: flex;
    gap: 40px;
    border-bottom: 1px solid ${Variables.ThemeLight.Border1};
    flex-wrap: wrap;

    > div {
        flex: 1;
        min-width: 200px;
    }
`;

export const SectionEyebrow = styled.div`
    font-size: 10px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextMild};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 9px;
`;

export const PatientName = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${Variables.ThemeLight.TextStrong};
`;

export const EnterPatientLink = styled.a`
    margin-top: 11px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    color: ${Variables.ThemeLight.PrimaryInverted3};
    border: 1px solid ${Variables.ThemeLight.PrimaryMild1};
    background: ${Variables.ThemeLight.PrimaryMild2};
    border-radius: 6px;
    padding: 7px 11px;
    text-decoration: none;
    width: fit-content;

    &:hover {
        background: ${Variables.ThemeLight.PrimaryMild1};
    }

    @media print {
        display: none;
    }
`;

export const StatementFooter = styled.div`
    margin-top: auto;
    padding: 22px 40px;
    border-top: 1px solid ${Variables.ThemeLight.Border1};
    background: ${Variables.ThemeLight.Background1};
    display: flex;
    gap: 40px;
    flex-wrap: wrap;
    flex-shrink: 0;

    > div {
        flex: 1;
        min-width: 200px;
    }
`;

export const FooterEyebrow = styled.div`
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    color: ${Variables.ThemeLight.TextMedium};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 9px;
`;

export const FooterBody = styled.div`
    font-size: 13px;
    line-height: 1.65;
    color: ${Variables.ThemeLight.TextMild};
`;

export const FooterAccent = styled.span`
    color: ${Variables.ThemeLight.PrimaryInverted3};
    font-weight: 500;
`;

export const LinesTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    padding: 0 40px;

    th,
    td {
        padding: 12px 40px;
        border-bottom: 1px solid ${Variables.ThemeLight.Background1};
        font-size: 14px;
        color: ${Variables.ThemeLight.TextStrong};
    }

    th {
        padding-top: 14px;
        padding-bottom: 9px;
        font-size: 10px;
        font-weight: 600;
        color: ${Variables.ThemeLight.TextMild};
        text-transform: uppercase;
        letter-spacing: 0.05em;
        border-bottom: 1px solid ${Variables.ThemeLight.Border1};
        text-align: left;
    }

    th[data-right='true'],
    td[data-right='true'] {
        text-align: right;
        font-variant-numeric: tabular-nums;
    }
`;

export const Totals = styled.div`
    padding: 18px 40px 26px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
`;

export const TotalRow = styled.div`
    display: flex;
    justify-content: space-between;
    width: 320px;
    max-width: 100%;
    padding: 7px 0;
    font-size: 13px;
    color: ${Variables.ThemeLight.TextMedium};
    font-variant-numeric: tabular-nums;
`;

export const DueBox = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    width: 320px;
    max-width: 100%;
    padding: 14px;
    margin-top: 12px;
    background: ${Variables.ThemeLight.PrimaryMild2};
    border: 1px solid ${Variables.ThemeLight.PrimaryMild1};
    border-radius: 9px;

    span {
        font-size: 14px;
        font-weight: 600;
        color: ${Variables.ThemeLight.TextStrong};
    }

    strong {
        font-size: 24px;
        font-weight: 700;
        color: ${Variables.ThemeLight.PrimaryConstant6};
        font-variant-numeric: tabular-nums;
    }
`;
